import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Zap, ArrowLeft, Trophy, Star, 
  RotateCcw, Lightbulb, Battery, CircuitBoard, Power
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const CircuitBuilderGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [currentChallenge, setCurrentChallenge] = useState(0);
  const [placedComponents, setPlacedComponents] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [circuitComplete, setCircuitComplete] = useState(false);
  const [score, setScore] = useState(0);
  const [completedCircuits, setCompletedCircuits] = useState(0);

  const challenges = [
    {
      id: 1,
      name: 'Simple Light Circuit',
      description: 'Connect a battery to a light bulb to make it glow!',
      goal: 'Make the bulb light up',
      requiredComponents: ['battery', 'wire', 'bulb'],
      availableComponents: ['battery', 'wire', 'bulb', 'switch', 'motor'],
      hint: 'A complete circuit needs: power source → wire → device → wire → back to power source',
      explanation: 'Electricity flows from the battery through the wire to the bulb, making it light up!',
      points: 25
    },
    {
      id: 2,
      name: 'Switch Control',
      description: 'Add a switch to control when the light turns on and off',
      goal: 'Create a circuit with an on/off switch',
      requiredComponents: ['battery', 'wire', 'switch', 'bulb'],
      availableComponents: ['battery', 'wire', 'bulb', 'switch', 'motor', 'buzzer'],
      hint: 'The switch breaks the circuit when open, stopping the flow of electricity',
      explanation: 'Switches act as gates - when closed, electricity flows. When open, the circuit is broken!',
      points: 30
    },
    {
      id: 3,
      name: 'Series Circuit',
      description: 'Connect two bulbs in series - one after another',
      goal: 'Light up two bulbs in a row',
      requiredComponents: ['battery', 'wire', 'bulb', 'bulb'],
      availableComponents: ['battery', 'wire', 'bulb', 'bulb', 'switch', 'motor'],
      hint: 'In a series circuit, components are connected end-to-end in a single path',
      explanation: 'In series circuits, current flows through each component. If one bulb breaks, both go out!',
      points: 35
    },
    {
      id: 4,
      name: 'Motor Power',
      description: 'Create a circuit to spin a motor',
      goal: 'Make the motor spin',
      requiredComponents: ['battery', 'wire', 'motor'],
      availableComponents: ['battery', 'wire', 'bulb', 'motor', 'switch', 'buzzer'],
      hint: 'Motors convert electrical energy into motion (kinetic energy)',
      explanation: 'Electric motors use magnetism to convert electricity into rotational motion!',
      points: 25
    },
    {
      id: 5,
      name: 'Alarm Circuit',
      description: 'Build a circuit with a buzzer for an alarm',
      goal: 'Make the buzzer sound',
      requiredComponents: ['battery', 'wire', 'switch', 'buzzer'],
      availableComponents: ['battery', 'wire', 'bulb', 'buzzer', 'switch', 'motor'],
      hint: 'Buzzers convert electrical energy into sound energy',
      explanation: 'When electricity flows through a buzzer, it vibrates to create sound waves!',
      points: 30
    },
    {
      id: 6,
      name: 'Parallel Circuit',
      description: 'Connect two bulbs in parallel - each with its own path',
      goal: 'Light two bulbs independently',
      requiredComponents: ['battery', 'wire', 'wire', 'bulb', 'bulb'],
      availableComponents: ['battery', 'wire', 'wire', 'bulb', 'bulb', 'switch'],
      hint: 'In parallel, each bulb has its own path to the battery',
      explanation: 'Parallel circuits have multiple paths - if one bulb breaks, others still work! This is how houses are wired.',
      points: 40
    }
  ];

  const currentChallengeData = challenges[currentChallenge];

  const componentIcons = {
    battery: '🔋',
    wire: '➰',
    bulb: '💡',
    switch: '🔘',
    motor: '⚙️',
    buzzer: '🔔'
  };

  const startGame = () => {
    setGameState('playing');
    setCurrentChallenge(0);
    setPlacedComponents([]);
    setShowResult(false);
    setCircuitComplete(false);
    setScore(0);
    setCompletedCircuits(0);
  };

  const addComponent = (component) => {
    if (showResult) return;
    setPlacedComponents([...placedComponents, component]);
  };

  const removeComponent = (index) => {
    if (showResult) return;
    setPlacedComponents(placedComponents.filter((_, i) => i !== index));
  };

  const clearCircuit = () => {
    setPlacedComponents([]);
    setCircuitComplete(false);
  };

  const testCircuit = () => {
    // Check if placed components match required components
    const required = [...currentChallengeData.requiredComponents].sort();
    const placed = [...placedComponents].sort();
    
    const isCorrect = required.length === placed.length && 
                      required.every((val, idx) => val === placed[idx]);

    setShowResult(true);
    
    if (isCorrect) {
      setCircuitComplete(true);
      setScore(s => s + currentChallengeData.points);
      setCompletedCircuits(c => c + 1);
      toast.success('Circuit works! Great job!');
    } else {
      setCircuitComplete(false);
      toast.error('Circuit incomplete. Check your components!');
    }
  };

  const nextChallenge = () => {
    if (currentChallenge < challenges.length - 1) {
      setCurrentChallenge(c => c + 1);
      setPlacedComponents([]);
      setShowResult(false);
      setCircuitComplete(false);
    } else {
      endGame();
    }
  };

  const endGame = async () => {
    setGameState('finished');
    
    try {
      await api.post('/games/complete', {
        game_id: 'circuit-builder',
        score: score,
        metadata: { 
          circuits_completed: completedCircuits,
          total_circuits: challenges.length
        }
      });
      toast.success(`⚡ You earned ${score} points!`);
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  return (
    <StudentLayout>
      <div data-testid="circuit-builder-game" className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
        </div>

        {/* Game Header */}
        <div className="student-card bg-amber-100 border-amber-300 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Circuit Builder</h1>
                <p className="text-slate-600">
                  {gameState === 'playing' 
                    ? `Challenge ${currentChallenge + 1} of ${challenges.length}` 
                    : 'Build electrical circuits!'}
                </p>
              </div>
            </div>
            {gameState === 'playing' && (
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-500" />
                <span className="font-heading text-xl font-bold text-slate-800">{score}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-4">Circuit Builder</h2>
            <p className="text-lg text-slate-600 mb-8">
              Learn about electricity by building circuits!<br/>
              Connect components to complete each challenge.
            </p>
            <div className="flex justify-center gap-6 mb-8">
              <div className="text-center">
                <Battery className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Power sources</p>
              </div>
              <div className="text-center">
                <Lightbulb className="w-10 h-10 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Light & sound</p>
              </div>
              <div className="text-center">
                <CircuitBoard className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-slate-500">Build circuits</p>
              </div>
            </div>
            <Button onClick={startGame} className="bg-amber-500 hover:bg-amber-600 text-white text-lg px-10 py-6">
              Start Building
              <Zap className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentChallengeData && (
          <div className="student-card">
            {/* Challenge Info */}
            <div className="mb-6">
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-2">
                {currentChallengeData.name}
              </h2>
              <p className="text-slate-600 mb-2">{currentChallengeData.description}</p>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-amber-800 font-semibold">🎯 Goal: {currentChallengeData.goal}</p>
              </div>
            </div>

            {/* Circuit Board */}
            <div className="bg-slate-800 rounded-xl p-6 mb-6 min-h-[150px]">
              <p className="text-slate-400 text-sm mb-4">Your Circuit:</p>
              <div className="flex flex-wrap gap-3 items-center justify-center">
                {placedComponents.length === 0 ? (
                  <p className="text-slate-500">Click components below to add them</p>
                ) : (
                  placedComponents.map((comp, idx) => (
                    <div 
                      key={idx}
                      onClick={() => removeComponent(idx)}
                      className="flex flex-col items-center bg-slate-700 rounded-lg p-3 cursor-pointer hover:bg-slate-600 transition-colors"
                    >
                      <span className="text-3xl">{componentIcons[comp]}</span>
                      <span className="text-xs text-slate-300 mt-1 capitalize">{comp}</span>
                    </div>
                  ))
                )}
                {placedComponents.length > 0 && circuitComplete && (
                  <div className="ml-4 text-4xl animate-pulse">⚡</div>
                )}
              </div>
            </div>

            {/* Available Components */}
            <div className="mb-6">
              <p className="text-sm text-slate-500 mb-3">Available Components (click to add):</p>
              <div className="flex flex-wrap gap-2">
                {currentChallengeData.availableComponents.map((comp, idx) => (
                  <button
                    key={idx}
                    onClick={() => addComponent(comp)}
                    disabled={showResult}
                    className="flex items-center gap-2 bg-white border-2 border-slate-200 hover:border-amber-400 rounded-lg px-4 py-2 transition-colors disabled:opacity-50"
                  >
                    <span className="text-xl">{componentIcons[comp]}</span>
                    <span className="capitalize text-sm font-medium">{comp}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Hint */}
            {!showResult && (
              <div className="bg-blue-50 rounded-xl p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  💡 <strong>Hint:</strong> {currentChallengeData.hint}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            {!showResult ? (
              <div className="flex gap-3">
                <Button variant="outline" onClick={clearCircuit} className="flex-1">
                  Clear Circuit
                </Button>
                <Button 
                  onClick={testCircuit}
                  disabled={placedComponents.length === 0}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Power className="w-4 h-4 mr-2" />
                  Test Circuit
                </Button>
              </div>
            ) : (
              <div>
                <div className={`rounded-xl p-4 mb-4 ${circuitComplete ? 'bg-emerald-100' : 'bg-red-100'}`}>
                  <p className={`font-semibold mb-2 ${circuitComplete ? 'text-emerald-800' : 'text-red-800'}`}>
                    {circuitComplete ? '✅ Circuit Complete!' : '❌ Circuit Not Working'}
                  </p>
                  <p className={circuitComplete ? 'text-emerald-700' : 'text-red-700'}>
                    {currentChallengeData.explanation}
                  </p>
                  {circuitComplete && (
                    <p className="mt-2 font-bold text-emerald-800">+{currentChallengeData.points} points!</p>
                  )}
                </div>
                {!circuitComplete ? (
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => { setShowResult(false); clearCircuit(); }} className="flex-1">
                      Try Again
                    </Button>
                    <Button onClick={nextChallenge} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white">
                      Skip Challenge
                    </Button>
                  </div>
                ) : (
                  <Button onClick={nextChallenge} className="w-full bg-amber-500 hover:bg-amber-600 text-white">
                    {currentChallenge < challenges.length - 1 ? 'Next Challenge' : 'See Results'}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">Engineer Complete!</h2>
            <p className="text-xl text-slate-600 mb-4">
              You built {completedCircuits} out of {challenges.length} circuits!
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-600">+{score}</span>
              <span className="text-slate-600 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-amber-500 hover:bg-amber-600 text-white">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default CircuitBuilderGame;
